/**
 * As fotografias da carteira e o que há dentro da escolhida.
 *
 * A ordem da tela é a da conclusão, não a do processamento: primeiro **de quem é** a carteira
 * (exposição por grupo econômico, com a régua na mão do operador), depois quem mais deve, e só
 * então a lista de títulos. Abrir pela lista faria o operador somar 299 linhas de cabeça para
 * chegar ao número que a primeira seção já dá.
 *
 * O seletor de régua fica visível pelo mesmo motivo do `PainelNucleo`: o mesmo estoque tem dois
 * grupos a 0,85 e um a 0,60, e um retrato que não diz sob que régua foi tirado não é um retrato.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Chip, Spinner } from '@/components/ui/misc';
import { Barras, FaixaParticipacao, Indicador, Miniserie } from '@/components/graficos';
import { api } from '@/lib/api';

type Posicao = {
  id: string;
  data_referencia: string;
  titulos: number;
  somado: number;
  total_nominal: number | null;
  importacao_id: string | null;
};

type Parte = { tipo: string; id: string; rotulo: string; rota: string };

type TituloLinha = {
  id: string;
  seu_numero: string | null;
  tipo_recebivel: string | null;
  cedente: Parte;
  sacado: Parte;
  valor_nominal: number | null;
  valor_presente: number | null;
  situacao: string | null;
  faixa_pdd: string | null;
  venc_original: string | null;
  taxa_cessao: number | null;
};

type Grupo = {
  nucleo_id: string | null;
  rotulo: string;
  fatia: number;
  titulos: number;
  valor_nominal: number;
  valor_vencido: number;
  sacados: number;
  sacados_do_grupo: number;
  valor_intragrupo: number;
  cedentes: Parte[];
};

type Exposicao = {
  data_referencia: string;
  limiar: number;
  titulos: number;
  valor_nominal: number;
  cedentes: number;
  nucleos: number;
  grupos: Grupo[];
  maiores_sacados: {
    chave: string;
    rotulo: string;
    documentos: number;
    titulos: number;
    valor_nominal: number;
    fatia: number;
  }[];
};

const reais = (v: number | null) =>
  v == null
    ? '—'
    : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

const pct = (v: number) => `${v.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
const dia = (iso: string | null) => (iso ? iso.split('-').reverse().join('/') : '—');

/** As mesmas três réguas do `PainelNucleo`, nomeadas pelo que ADMITEM. «0,60» não diz nada a quem
 *  lê; «admite endereço em comum» diz exatamente o que está sendo aceito como prova. */
const REGUAS = [
  { valor: 0.5, rotulo: 'Frouxa', ajuda: 'admite telefone, e-mail ou endereço em comum' },
  { valor: 0.75, rotulo: 'Padrão', ajuda: 'exige e-mail em comum ou laço societário' },
  { valor: 0.85, rotulo: 'Estrita', ajuda: 'só laço societário: sócio, participação ou mesma raiz' },
];

export function PainelPosicoesCarteira({ carteiraId }: { carteiraId: string }) {
  const [posicoes, setPosicoes] = useState<Posicao[] | null>(null);
  const [escolhida, setEscolhida] = useState<string | null>(null);
  const [limiar, setLimiar] = useState(0.75);
  const [exposicao, setExposicao] = useState<Exposicao | null>(null);
  const [titulos, setTitulos] = useState<{ total: number; itens: TituloLinha[] } | null>(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    let vivo = true;
    void api.get<Posicao[]>(`/cadastro/carteiras/${carteiraId}/posicoes`).then((r) => {
      if (!vivo) return;
      setCarregando(false);
      if (!r.ok) return void toast.error(r.erro);
      setPosicoes(r.dados);
      // A mais recente já vem escolhida: é a que responde à pergunta de hoje.
      setEscolhida(r.dados[0]?.id ?? null);
    });
    return () => {
      vivo = false;
    };
  }, [carteiraId]);

  const carregarPosicao = useCallback(async () => {
    if (!escolhida) return;
    const [e, t] = await Promise.all([
      api.get<Exposicao>(`/cadastro/carteiras/posicoes/${escolhida}/exposicao?limiar=${limiar}`),
      api.get<{ total: number; itens: TituloLinha[] }>(
        `/cadastro/carteiras/posicoes/${escolhida}/titulos`,
      ),
    ]);
    if (e.ok) setExposicao(e.dados);
    else toast.error(e.erro);
    if (t.ok) setTitulos(t.dados);
  }, [escolhida, limiar]);

  useEffect(() => {
    void carregarPosicao();
  }, [carregarPosicao]);

  if (carregando) return <Spinner />;
  if (!posicoes?.length) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="tipo-corpo-pequeno text-text-tertiary">
            Nenhuma posição importada ainda. Use «Importar estoque» para subir a fotografia da
            carteira — o formato não precisa ser o do regulador.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Fotografias da carteira</CardTitle>
          <p className="text-[13px] leading-[1.5] text-text-secondary">
            Cada linha é o estoque numa data. O total <em>declarado</em> pelo arquivo aparece ao
            lado do <em>somado</em> dos títulos: quando eles divergem, isso é um achado sobre o
            arquivo.
          </p>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          {posicoes.length > 1 && (
            <div className="flex items-center gap-2">
              <span className="tipo-rotulo text-text-tertiary">Somado, no tempo</span>
              <Miniserie
                // Da mais antiga para a mais nova — a lista vem ao contrário, para a fotografia
                // atual ficar em cima.
                valores={[...posicoes].reverse().map((p) => p.somado)}
                rotuloInicio={dia(posicoes[posicoes.length - 1].data_referencia)}
                rotuloFim={reais(posicoes[0].somado)}
              />
            </div>
          )}
          {posicoes.map((p) => {
            const diverge =
              p.total_nominal != null && Math.abs(p.total_nominal - p.somado) > 0.01;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setEscolhida(p.id)}
                className={`flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded-md border px-3 py-2 text-left transition-colors ${
                  escolhida === p.id
                    ? 'border-accent'
                    : 'border-border hover:border-text-tertiary'
                }`}
              >
                <span className="tipo-dado font-semibold text-text">{dia(p.data_referencia)}</span>
                <span className="tipo-corpo-pequeno text-text-secondary">
                  {p.titulos} títulos · {reais(p.somado)}
                </span>
                {p.total_nominal != null && (
                  <span
                    className={`tipo-corpo-pequeno ${diverge ? 'text-warning' : 'text-text-tertiary'}`}
                  >
                    declarado {reais(p.total_nominal)}
                    {diverge && ` · diferença de ${reais(Math.abs(p.total_nominal - p.somado))}`}
                  </span>
                )}
              </button>
            );
          })}
        </CardContent>
      </Card>

      {exposicao && (
        <Card>
          <CardHeader>
            <CardTitle>De quem é esta carteira</CardTitle>
            <p className="text-[13px] leading-[1.5] text-text-secondary">
              {/* A frase honesta, e nunca «catorze viraram dois» — nada foi fundido: as empresas
                  continuam de pé, o que existe é o agrupamento. */}
              <span className="font-semibold text-text">
                {exposicao.cedentes} cedentes, {exposicao.nucleos}{' '}
                {exposicao.nucleos === 1 ? 'grupo econômico' : 'grupos econômicos'}
              </span>{' '}
              — {reais(exposicao.valor_nominal)} em {exposicao.titulos} títulos.
            </p>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              {REGUAS.map((r) => (
                <button
                  key={r.valor}
                  type="button"
                  title={r.ajuda}
                  onClick={() => setLimiar(r.valor)}
                  className={`tipo-rotulo rounded-md border px-2 py-1 transition-colors ${
                    limiar === r.valor
                      ? 'border-accent text-accent'
                      : 'border-border text-text-tertiary hover:text-text'
                  }`}
                >
                  {r.rotulo}
                </button>
              ))}
              <span className="tipo-corpo-pequeno text-text-tertiary">
                {REGUAS.find((r) => r.valor === limiar)?.ajuda}
              </span>
            </div>

            {/* A leitura de relance, antes da lista: a faixa mostra COMO o todo se reparte, e os
                indicadores dão os números que a frase do cabeçalho resume. */}
            <FaixaParticipacao
              fatias={exposicao.grupos.map((g) => ({
                rotulo: g.rotulo,
                valor: g.valor_nominal,
                neutro: g.nucleo_id === null,
              }))}
            />

            <div className="flex flex-wrap gap-x-8 gap-y-3">
              <Indicador
                rotulo="Maior grupo"
                valor={pct(exposicao.grupos[0]?.fatia ?? 0)}
                detalhe={exposicao.grupos[0]?.rotulo}
              />
              <Indicador
                rotulo="Contra o próprio grupo"
                valor={reais(
                  exposicao.grupos.reduce((s, g) => s + g.valor_intragrupo, 0),
                )}
                detalhe="recebível cujo sacado é do grupo do cedente"
                alerta={exposicao.grupos.some((g) => g.valor_intragrupo > 0)}
              />
              <Indicador
                rotulo="Vencido"
                valor={reais(exposicao.grupos.reduce((s, g) => s + g.valor_vencido, 0))}
                detalhe="na data desta fotografia"
              />
            </div>

            <div className="flex flex-col">
              {exposicao.grupos.map((g, i) => (
                <div key={g.nucleo_id ?? i} className="border-t border-border py-2 first:border-t-0">
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className="tipo-dado font-semibold text-text">{pct(g.fatia)}</span>
                    <span className="text-text">{g.rotulo}</span>
                    <span className="tipo-corpo-pequeno text-text-tertiary">
                      {reais(g.valor_nominal)} · {g.titulos} títulos · {g.sacados} sacados
                    </span>
                    {g.cedentes.length > 1 && (
                      <Chip className="border-border text-text-secondary">
                        {g.cedentes.length} cedentes
                      </Chip>
                    )}
                  </div>
                  {g.cedentes.length > 1 && (
                    <p className="tipo-corpo-pequeno mt-1 text-text-tertiary">
                      {g.cedentes.map((c, j) => (
                        <span key={c.id}>
                          {j > 0 && ' · '}
                          <Link to={c.rota} className="text-accent hover:underline">
                            {c.rotulo}
                          </Link>
                        </span>
                      ))}
                    </p>
                  )}
                  {g.valor_intragrupo > 0 && (
                    <p className="tipo-corpo-pequeno mt-1 text-warning">
                      {reais(g.valor_intragrupo)} com o sacado dentro do próprio grupo (
                      {g.sacados_do_grupo} de {g.sacados})
                    </p>
                  )}
                </div>
              ))}
            </div>

            {exposicao.maiores_sacados.length > 0 && (
              <details className="border-t border-border pt-2">
                <summary className="tipo-rotulo cursor-pointer select-none text-text-secondary hover:text-text">
                  Maiores devedores ({exposicao.maiores_sacados.length})
                </summary>
                <p className="tipo-corpo-pequeno mt-2 text-text-tertiary">
                  Agrupados por raiz de CNPJ: matriz e filial são a mesma pessoa jurídica, não dois
                  devedores.
                </p>
                <div className="mt-2">
                  <Barras
                    itens={exposicao.maiores_sacados.map((sx) => ({
                      rotulo:
                        sx.documentos > 1
                          ? `${sx.rotulo} (${sx.documentos} CNPJs da mesma raiz)`
                          : sx.rotulo,
                      valor: sx.valor_nominal,
                      // O número ao lado da barra, no DOM — nunca só no hover.
                      texto: `${reais(sx.valor_nominal)} · ${pct(sx.fatia)}`,
                    }))}
                  />
                </div>
              </details>
            )}
          </CardContent>
        </Card>
      )}

      {titulos && (
        <Card>
          <CardHeader>
            <CardTitle>Títulos</CardTitle>
            <p className="text-[13px] leading-[1.5] text-text-secondary">
              Os maiores primeiro. Mostrando {titulos.itens.length} de {titulos.total} — o número
              inteiro fica ao lado porque truncar em silêncio é apresentar um recorte como se fosse
              o conjunto.
            </p>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="tipo-rotulo text-text-tertiary">
                  <th className="py-1 pr-3">Título</th>
                  <th className="py-1 pr-3">Cedente</th>
                  <th className="py-1 pr-3">Sacado</th>
                  <th className="py-1 pr-3 text-right">Nominal</th>
                  <th className="py-1 pr-3">Vencimento</th>
                  <th className="py-1">Situação</th>
                </tr>
              </thead>
              <tbody>
                {titulos.itens.map((t) => (
                  <tr key={t.id} className="border-t border-border">
                    <td className="tipo-dado py-1.5 pr-3 text-text-secondary">
                      {t.seu_numero || '—'}
                    </td>
                    <td className="py-1.5 pr-3">
                      <Link to={t.cedente.rota} className="text-accent hover:underline">
                        {t.cedente.rotulo}
                      </Link>
                    </td>
                    <td className="py-1.5 pr-3">
                      <Link to={t.sacado.rota} className="text-accent hover:underline">
                        {t.sacado.rotulo}
                      </Link>
                    </td>
                    <td className="tipo-dado py-1.5 pr-3 text-right text-text">
                      {reais(t.valor_nominal)}
                    </td>
                    <td className="tipo-dado py-1.5 pr-3 text-text-secondary">
                      {dia(t.venc_original)}
                    </td>
                    <td className="tipo-corpo-pequeno py-1.5 text-text-secondary">
                      {t.situacao || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <div>
        <Button size="sm" variant="fantasma" onClick={() => void carregarPosicao()}>
          Recarregar
        </Button>
      </div>
    </div>
  );
}
